// AdminSheetTemplatesPanel.tsx
//
// Editing the built-in raid sheet templates. These used to be ~900 lines of
// TypeScript, so adding an instance meant a code change and a deploy that only
// one person could do. Now they are rows, and this is the screen that edits
// them.
//
// The layout mirrors the data, because the split IS the design:
//
//   BOXES       what a box is: a label and an ordered list of duty rows.
//               "Healing" is one box that twenty encounters share.
//   ENCOUNTERS  where boxes go: the boss names it matches and the ordered
//               list of boxes it shows.
//
// A boss that needs different rows gets its OWN box rather than a diff against
// the shared one, which is why the box list is long and the encounter list is
// just keys. That was a deliberate call: patch semantics make every read a
// three-way merge and every edit a guess about intent.

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { tc, useLegible } from "./theme/legible";
type Slot = {
  key: string; label: string; short: string;
  marker?: number | null; markers?: number[]; note?: string; roleKey?: string;
  rule: { roles?: string[]; classes?: string[]; tags?: string[];
          preferRoles?: string[]; preferClasses?: string[] };
  target?: any;
};
type Box = {
  id: number; box_key: string; lane_key: string; label: string;
  accent: string; note: string | null; slots: Slot[];
};
type Layout = {
  id: number; raid_option_id: number | null; encounter_key: string;
  kind: string; name: string | null; match_keys: string[];
  note: string | null; notes: string[]; box_keys: string[];
  trash_before: { name: string; notes: string[]; box_keys: string[] } | null;
  sort_order: number;
};
type Payload = {
  boxes: Box[]; layouts: Layout[]; usage: Record<string, string[]>;
  raids: { id: number; label: string }[];
  nightRoles: { role_key: string; label: string; short: string }[];
  tags: { tag_key: string; tag_label: string }[];
};

const ACCENTS = ["tank", "healer", "dps", "utility", "control"];
const ROLES = ["tank", "healer", "melee", "ranged"];
const ACCENT_COLOR: Record<string, string> = {
  tank: "#38bdf8", healer: "#34d399", dps: "#fb923c",
  utility: "#a78bfa", control: "#f59e0b",
};

const FIELD = "rounded-md border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1 text-xs " +
  "text-[var(--c-ink)] outline-none focus:border-amber-400/50";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--c-ink-3)]";
const BTN = "rounded-md px-2 py-1 text-[11px] font-semibold transition-colors";

export default function AdminSheetTemplatesPanel() {
  useLegible();
  const [games, setGames] = useState<{ id: string; name: string }[]>([]);
  const [gameId, setGameId] = useState("");
  const [data, setData] = useState<Payload | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  const [tab, setTab] = useState<"boxes" | "encounters">("encounters");
  const [editBox, setEditBox] = useState<Box | null>(null);
  const [editLayout, setEditLayout] = useState<Layout | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetch("/games", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: any[]) => {
        const list = (Array.isArray(rows) ? rows : []).map((g) => ({
          id: String(g.id), name: g.name || g.code || "Game",
        }));
        setGames(list);
        if (list[0]) setGameId(list[0].id);
      })
      .catch(() => setMsg({ tone: "bad", text: "Could not list games." }));
  }, []);

  const load = useCallback(() => {
    if (!gameId) return;
    setBusy(true);
    fetch(`/admin/sheet-templates/${encodeURIComponent(gameId)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("load failed"))))
      .then((d) => setData(d))
      .catch(() => setMsg({ tone: "bad", text: "Could not load templates." }))
      .finally(() => setBusy(false));
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  const save = async (path: string, body: any, method = "PUT") => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`/admin/sheet-templates/${encodeURIComponent(gameId)}${path}`, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: method === "DELETE" ? undefined : JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMsg({ tone: "bad", text: j.detail || j.error || "Save failed." });
        return false;
      }
      setMsg({ tone: "ok", text: "Saved." });
      load();
      return true;
    } catch {
      setMsg({ tone: "bad", text: "Save failed." });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const raidLabel = useCallback((id: number | null) => {
    if (id == null) return "Any raid";
    const r = (data?.raids || []).filter((x) => x.id === id)[0];
    return r ? r.label : `#${id}`;
  }, [data]);

  const boxByKey = useMemo(() => {
    const m: Record<string, Box> = {};
    (data?.boxes || []).forEach((b) => { m[b.box_key] = b; });
    return m;
  }, [data]);

  const shownLayouts = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (data?.layouts || []).filter((l) =>
      !f || l.encounter_key.indexOf(f) >= 0
      || (l.match_keys || []).some((m) => m.indexOf(f) >= 0));
  }, [data, filter]);

  const shownBoxes = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (data?.boxes || []).filter((b) =>
      !f || b.box_key.toLowerCase().indexOf(f) >= 0
      || b.label.toLowerCase().indexOf(f) >= 0);
  }, [data, filter]);

  return (
    <div className="space-y-4" data-testid="admin-sheet-templates">
      <div className="flex flex-wrap items-center gap-2">
        <select value={gameId} onChange={(e) => setGameId(e.target.value)} className={FIELD}>
          {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <div className="flex items-center gap-1 rounded-full border border-[var(--c-line)] bg-[var(--c-surface)] p-1">
          {(["encounters", "boxes"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                tab === t ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"}`}>
              {t === "encounters" ? "Encounters" : "Boxes"}
            </button>
          ))}
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Filter" className={`${FIELD} w-40`} />
        <button onClick={() => (tab === "boxes"
          ? setEditBox({ id: 0, box_key: "", lane_key: "", label: "",
                         accent: "utility", note: null, slots: [] })
          : setEditLayout({ id: 0, raid_option_id: null, encounter_key: "",
                            kind: "boss", name: null, match_keys: [], note: null,
                            notes: [], box_keys: [], trash_before: null, sort_order: 100 }))}
          className={`${BTN} bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)]`}>
          New {tab === "boxes" ? "box" : "encounter"}
        </button>
        {busy && <span className="text-[11px] text-[var(--c-ink-3)]">Working</span>}
        {msg && (
          <span className={`text-[11px] ${msg.tone === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
            {msg.text}
          </span>
        )}
      </div>

      <p className="max-w-3xl text-[11px] leading-relaxed text-[var(--c-ink-3)]">
        These are the built-in templates every guild starts from. A guild that
        overrides one keeps its own copy and still receives changes to
        everything else. Editing here needs no deploy.
      </p>

      {tab === "encounters" && (
        <div className="overflow-hidden rounded-lg border border-[var(--c-line)]">
          <table className="w-full text-left text-xs">
            <thead className="bg-[var(--c-bg-veil)] text-[var(--c-ink-3)]">
              <tr>
                <th className="px-3 py-2 font-semibold">Encounter</th>
                <th className="px-3 py-2 font-semibold">Raid</th>
                <th className="px-3 py-2 font-semibold">Matches</th>
                <th className="px-3 py-2 font-semibold">Boxes</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shownLayouts.map((l) => (
                <tr key={l.id} data-testid={`layout-${l.encounter_key}`}
                    className="border-t border-[var(--c-line-soft)] hover:bg-[var(--c-bg-veil)]">
                  <td className="px-3 py-1.5">
                    <span className="font-semibold text-[var(--c-ink)]">{l.encounter_key}</span>
                    <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[var(--c-ink-4)]">
                      {l.kind}
                    </span>
                    {l.trash_before && (
                      <span className="ml-1.5 rounded bg-[var(--c-sunken)] px-1 text-[9px] text-[var(--c-ink-3)]">
                        + wave block
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-[var(--c-ink-3)]">{raidLabel(l.raid_option_id)}</td>
                  <td className="px-3 py-1.5 text-[var(--c-ink-3)]">{(l.match_keys || []).join(", ") || "·"}</td>
                  <td className="px-3 py-1.5">
                    <span className="flex flex-wrap gap-1">
                      {(l.box_keys || []).map((k) => (
                        <span key={k} className="rounded px-1.5 py-[1px] text-[10px]"
                              style={{
                                color: tc(ACCENT_COLOR[boxByKey[k]?.accent] || "#94a3b8"),
                                background: `${ACCENT_COLOR[boxByKey[k]?.accent] || "#94a3b8"}1A`,
                              }}>
                          {boxByKey[k]?.label || k}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => setEditLayout(l)}
                            className="text-[11px] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "boxes" && (
        <div className="overflow-hidden rounded-lg border border-[var(--c-line)]">
          <table className="w-full text-left text-xs">
            <thead className="bg-[var(--c-bg-veil)] text-[var(--c-ink-3)]">
              <tr>
                <th className="px-3 py-2 font-semibold">Box</th>
                <th className="px-3 py-2 font-semibold">Lane</th>
                <th className="px-3 py-2 font-semibold">Rows</th>
                <th className="px-3 py-2 font-semibold">Used by</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {shownBoxes.map((b) => {
                const used = (data?.usage || {})[b.box_key] || [];
                return (
                  <tr key={b.id} data-testid={`box-${b.box_key}`}
                      className="border-t border-[var(--c-line-soft)] hover:bg-[var(--c-bg-veil)]">
                    <td className="px-3 py-1.5">
                      <span className="font-semibold"
                            style={{ color: tc(ACCENT_COLOR[b.accent] || "#e2e8f0") }}>
                        {b.label}
                      </span>
                      <span className="ml-1.5 text-[10px] text-[var(--c-ink-4)]">{b.box_key}</span>
                    </td>
                    <td className="px-3 py-1.5 text-[var(--c-ink-3)]">{b.lane_key}</td>
                    <td className="px-3 py-1.5 text-[var(--c-ink-3)]">{(b.slots || []).length}</td>
                    <td className="px-3 py-1.5 text-[var(--c-ink-3)]">
                      {used.length === 0
                        ? <span className="text-[var(--c-ink-4)]">unused</span>
                        : used.length <= 3 ? used.join(", ") : `${used.length} encounters`}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => setEditBox(b)}
                              className="text-[11px] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editBox && (
        <BoxEditor
          box={editBox}
          tags={data?.tags || []}
          nightRoles={data?.nightRoles || []}
          usedBy={(data?.usage || {})[editBox.box_key] || []}
          onClose={() => setEditBox(null)}
          onSave={async (b) => { if (await save("/box", b)) setEditBox(null); }}
          onDelete={async () => {
            if (await save(`/box/${encodeURIComponent(editBox.box_key)}`, null, "DELETE")) {
              setEditBox(null);
            }
          }}
        />
      )}

      {editLayout && (
        <LayoutEditor
          layout={editLayout}
          boxes={data?.boxes || []}
          raids={data?.raids || []}
          onClose={() => setEditLayout(null)}
          onSave={async (l) => { if (await save("/layout", l)) setEditLayout(null); }}
          onDelete={editLayout.id
            ? async () => {
                if (await save(`/layout/${editLayout.id}`, null, "DELETE")) setEditLayout(null);
              }
            : undefined}
        />
      )}
    </div>
  );
}

// ── Box editor ───────────────────────────────────────────────
function BoxEditor({ box, tags, nightRoles, usedBy, onClose, onSave, onDelete }: {
  box: Box;
  tags: { tag_key: string; tag_label: string }[];
  nightRoles: { role_key: string; label: string; short: string }[];
  usedBy: string[];
  onClose: () => void;
  onSave: (b: Box) => void;
  onDelete: () => void;
}) {
  const [b, setB] = useState<Box>(() => JSON.parse(JSON.stringify(box)));
  const isNew = !box.id;
  const set = (patch: Partial<Box>) => setB((p) => ({ ...p, ...patch }));
  const setSlot = (i: number, patch: Partial<Slot>) =>
    setB((p) => ({ ...p, slots: p.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  return (
    <Modal title={isNew ? "New box" : `Edit ${b.label}`} onClose={onClose}>
      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="Box key" hint="Unique id for this row">
          <input value={b.box_key} disabled={!isNew}
                 onChange={(e) => set({ box_key: e.target.value, lane_key: b.lane_key || e.target.value })}
                 className={`${FIELD} w-full disabled:opacity-50`} />
        </Field>
        <Field label="Lane" hint={isNew ? "What the sheet calls it" : "Fixed: overrides are stored against it"}>
          <input value={b.lane_key} disabled={!isNew}
                 onChange={(e) => set({ lane_key: e.target.value })}
                 className={`${FIELD} w-full disabled:opacity-50`} />
        </Field>
        <Field label="Accent">
          <select value={b.accent} onChange={(e) => set({ accent: e.target.value })}
                  className={`${FIELD} w-full`}>
            {ACCENTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
        <Field label="Label"><input value={b.label} onChange={(e) => set({ label: e.target.value })}
               className={`${FIELD} w-full`} /></Field>
        <Field label="Note" hint="Shown under the box header">
          <input value={b.note || ""} onChange={(e) => set({ note: e.target.value })}
                 className={`${FIELD} w-full`} />
        </Field>
      </div>

      {usedBy.length > 0 && (
        <p className="mt-2 text-[11px] text-[var(--c-ink-3)]">
          Used by {usedBy.length} encounter{usedBy.length === 1 ? "" : "s"}: {usedBy.slice(0, 6).join(", ")}
          {usedBy.length > 6 ? ` +${usedBy.length - 6}` : ""}. Editing changes all of them.
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <span className={LABEL}>Rows</span>
        <button onClick={() => setB((p) => ({
          ...p,
          slots: p.slots.concat([{ key: `slot_${p.slots.length + 1}`, label: "", short: "", rule: {} }]),
        }))} className={`${BTN} bg-[var(--c-sunken-2)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)]`}>+ row</button>
      </div>

      <div className="mt-1 space-y-1">
        {b.slots.map((s, i) => (
          <div key={i} data-testid={`slot-row-${i}`}
               className="grid grid-cols-[1fr_1fr_70px_1fr_1fr_28px] gap-1 rounded border border-[var(--c-line-soft)] bg-[var(--c-surface)] p-1">
            <input value={s.key} onChange={(e) => setSlot(i, { key: e.target.value })}
                   placeholder="key" className={FIELD} />
            <input value={s.label} onChange={(e) => setSlot(i, { label: e.target.value })}
                   placeholder="label" className={FIELD} />
            <input value={s.short} onChange={(e) => setSlot(i, { short: e.target.value })}
                   placeholder="short" className={FIELD} />
            <select value={s.roleKey || ""} onChange={(e) => setSlot(i, { roleKey: e.target.value })}
                    className={FIELD} title="Bind to a night role so it follows the night sheet">
              <option value="">no night role</option>
              {nightRoles.map((r) => (
                <option key={r.role_key} value={r.role_key}>{r.label}</option>
              ))}
            </select>
            <select
              value={(s.rule.tags || [])[0] || ""}
              onChange={(e) => setSlot(i, {
                rule: { ...s.rule, tags: e.target.value ? [e.target.value] : undefined },
              })}
              className={FIELD} title="Require an ability from the game's tag table">
              <option value="">any ability</option>
              {tags.map((t) => <option key={t.tag_key} value={t.tag_key}>{t.tag_label}</option>)}
            </select>
            <button onClick={() => setB((p) => ({ ...p, slots: p.slots.filter((_, j) => j !== i) }))}
                    className="text-[11px] text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300">×</button>
          </div>
        ))}
      </div>

      <Footer onClose={onClose} onSave={() => onSave(b)}
              onDelete={!isNew && usedBy.length === 0 ? onDelete : undefined}
              deleteHint={!isNew && usedBy.length > 0 ? "In use by an encounter" : undefined} />
    </Modal>
  );
}

// ── Encounter editor ─────────────────────────────────────────
function LayoutEditor({ layout, boxes, raids, onClose, onSave, onDelete }: {
  layout: Layout;
  boxes: Box[];
  raids: { id: number; label: string }[];
  onClose: () => void;
  onSave: (l: Layout) => void;
  onDelete?: () => void;
}) {
  const [l, setL] = useState<Layout>(() => JSON.parse(JSON.stringify(layout)));
  const isNew = !layout.id;
  const set = (patch: Partial<Layout>) => setL((p) => ({ ...p, ...patch }));
  const byKey: Record<string, Box> = {};
  boxes.forEach((b) => { byKey[b.box_key] = b; });

  const move = (i: number, d: number) => setL((p) => {
    const next = p.box_keys.slice();
    const j = i + d;
    if (j < 0 || j >= next.length) return p;
    [next[i], next[j]] = [next[j], next[i]];
    return { ...p, box_keys: next };
  });

  return (
    <Modal title={isNew ? "New encounter" : `Edit ${l.encounter_key}`} onClose={onClose}>
      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="Encounter key"><input value={l.encounter_key} disabled={!isNew}
               onChange={(e) => set({ encounter_key: e.target.value })}
               className={`${FIELD} w-full disabled:opacity-50`} /></Field>
        <Field label="Kind">
          <select value={l.kind} onChange={(e) => set({ kind: e.target.value })}
                  className={`${FIELD} w-full`}>
            {["boss", "trash", "general", "fallback"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Raid" hint="Any raid = applies everywhere">
          <select value={l.raid_option_id ?? ""}
                  onChange={(e) => set({ raid_option_id: e.target.value ? Number(e.target.value) : null })}
                  className={`${FIELD} w-full`}>
            <option value="">Any raid</option>
            {raids.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>
        <Field label="Matches" hint="Boss names, comma separated, lowercased">
          <input value={(l.match_keys || []).join(", ")}
                 onChange={(e) => set({ match_keys: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                 className={`${FIELD} w-full`} />
        </Field>
        <Field label="Note"><input value={l.note || ""} onChange={(e) => set({ note: e.target.value })}
               className={`${FIELD} w-full`} /></Field>
        <Field label="Order"><input type="number" value={l.sort_order}
               onChange={(e) => set({ sort_order: Number(e.target.value) })}
               className={`${FIELD} w-full`} /></Field>
      </div>

      <Field label="Bullets" hint="One per line. Shown as a Notes box on the sheet.">
        <textarea value={(l.notes || []).join("\n")} rows={4}
                  onChange={(e) => set({ notes: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
                  className={`${FIELD} mt-1 w-full`} />
      </Field>

      <div className="mt-3">
        <span className={LABEL}>Boxes, in order</span>
        <div className="mt-1 space-y-1">
          {(l.box_keys || []).map((k, i) => (
            <div key={`${k}-${i}`} className="flex items-center gap-1.5 rounded border border-[var(--c-line-soft)] bg-[var(--c-surface)] px-2 py-1">
              <span className="w-4 text-[10px] text-[var(--c-ink-4)]">{i + 1}</span>
              <span className="flex-1 text-xs font-semibold"
                    style={{ color: tc(ACCENT_COLOR[byKey[k]?.accent] || "#e2e8f0") }}>
                {byKey[k]?.label || k}
                <span className="ml-1.5 text-[10px] font-normal text-[var(--c-ink-4)]">{k}</span>
              </span>
              <button onClick={() => move(i, -1)} className="px-1 text-[10px] text-[var(--c-ink-4)] hover:text-[var(--c-ink)]">▲</button>
              <button onClick={() => move(i, 1)} className="px-1 text-[10px] text-[var(--c-ink-4)] hover:text-[var(--c-ink)]">▼</button>
              <button onClick={() => set({ box_keys: l.box_keys.filter((_, j) => j !== i) })}
                      className="px-1 text-[11px] text-[var(--c-ink-4)] hover:text-amber-700 dark:hover:text-amber-300">×</button>
            </div>
          ))}
        </div>
        <select value="" onChange={(e) => {
          if (!e.target.value) return;
          set({ box_keys: (l.box_keys || []).concat([e.target.value]) });
        }} className={`${FIELD} mt-1 w-full`}>
          <option value="">Add a box</option>
          {boxes.map((b) => (
            <option key={b.box_key} value={b.box_key}>{b.label} ({b.box_key})</option>
          ))}
        </select>
      </div>

      <Footer onClose={onClose} onSave={() => onSave(l)} onDelete={onDelete} />
    </Modal>
  );
}

// ── Bits ─────────────────────────────────────────────────────
function Modal({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-6">
      <div className="w-full max-w-4xl rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 shadow-2xl">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-sm font-bold">{title}</h3>
          <button onClick={onClose} className="ml-auto text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)]">close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className={LABEL}>{label}</span>
      {hint && <span className="ml-1.5 text-[10px] text-[var(--c-ink-4)]">{hint}</span>}
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function Footer({ onClose, onSave, onDelete, deleteHint }: {
  onClose: () => void; onSave: () => void;
  onDelete?: () => void; deleteHint?: string;
}) {
  return (
    <div className="mt-4 flex items-center gap-2 border-t border-[var(--c-line)] pt-3">
      {onDelete && (
        <button onClick={onDelete}
                className={`${BTN} border border-amber-400/30 text-amber-700/80 dark:text-amber-300/80 hover:bg-amber-400/10`}>
          Delete
        </button>
      )}
      {deleteHint && <span className="text-[10px] text-[var(--c-ink-4)]">{deleteHint}</span>}
      <button onClick={onClose} className={`${BTN} ml-auto text-[var(--c-ink-3)] hover:text-[var(--c-ink)]`}>Cancel</button>
      <button onClick={onSave} className={`${BTN} bg-[var(--c-accent)] text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)]`}>Save</button>
    </div>
  );
}
